import React from 'react';
import { Button } from './ui/button';
import { Grid, Layers, MousePointer } from 'lucide-react';

const MapEditorSection = () => {
  return (
    <section className="py-24 bg-gradient-to-b from-black via-purple-950/10 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Tweak Levels Visually with the
            <br />
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Tilemap &amp; Difficulty Editors</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            The AI builds it, you fine-tune it — paint tiles, place enemies and coins, and dial in the challenge without spending a single credit
          </p>
        </div>

        {/* Map Editor Preview */}
        <div className="relative">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 border border-white/10 shadow-2xl">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
              <div className="flex gap-4">
                <button className="flex items-center gap-2 px-4 py-2 bg-purple-600 rounded-lg text-white">
                  <MousePointer size={18} />
                  <span>Select</span>
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-gray-800 rounded-lg text-gray-400 hover:bg-gray-700">
                  <Grid size={18} />
                  <span>Tiles</span>
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-gray-800 rounded-lg text-gray-400 hover:bg-gray-700">
                  <Layers size={18} />
                  <span>Layers</span>
                </button>
              </div>
              <div className="flex gap-2">
                <button className="px-4 py-2 bg-gray-800 rounded-lg text-gray-400 hover:bg-gray-700">
                  Undo
                </button>
                <button className="px-4 py-2 bg-gray-800 rounded-lg text-gray-400 hover:bg-gray-700">
                  Redo
                </button>
              </div>
            </div>

            {/* Map Canvas */}
            <div className="bg-black/50 rounded-2xl p-8 aspect-video flex items-center justify-center border border-white/5">
              <div className="text-center">
                <div className="w-64 h-64 mx-auto mb-6 bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-2xl border-2 border-dashed border-purple-500/50 flex items-center justify-center">
                  <div>
                    <Grid size={64} className="text-purple-400 mx-auto mb-4" />
                    <p className="text-gray-400">Paint Tiles, Enemies &amp; Coins</p>
                  </div>
                </div>
                <p className="text-gray-500 text-sm">Every 2D Platformer session gets a paintable tilemap with per-level tabs — edits apply instantly and for free</p>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-3 gap-8 mt-12">
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Grid size={32} className="text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Tilemap Editor</h3>
            <p className="text-gray-400">Paintable grid with a brush palette — platforms, spikes, enemies, coins and flags</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Layers size={32} className="text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Per-Level Tabs</h3>
            <p className="text-gray-400">Multi-level games get one tab per level, so every stage is tweakable on its own</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <MousePointer size={32} className="text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Difficulty Sliders</h3>
            <p className="text-gray-400">Gravity, speed, enemy pace and more — balance the challenge with five simple sliders</p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Button
            size="lg"
            data-testid="mapeditor-try-free-btn"
            onClick={() => window.location.href = '/create'}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8"
          >
            Try it Free
          </Button>
        </div>
      </div>
    </section>
  );
};

export default MapEditorSection;